import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, Box, inputLabelClasses, TextField, useTheme } from '@mui/material';
import { normalizeTrimmedValue, shouldTrimFieldValue } from '@ui/components/common/TextField/text-input-normalize';
import FormValidationMessage from '@ui/components/FormValidationMessage';
import React from 'react';
import { useTranslation } from 'react-i18next';
const filledInputAutofillStyles = {
    '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
        WebkitTextFillColor: 'inherit',
        caretColor: 'inherit',
        transition: 'background-color 5000s ease-in-out 0s'
    },
    '& input:-webkit-autofill:hover': {
        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
        WebkitTextFillColor: 'inherit'
    },
    '& input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
        WebkitTextFillColor: 'inherit'
    },
    '& input:-webkit-autofill:active': {
        WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
        WebkitTextFillColor: 'inherit'
    }
};
const HFilledTextField = React.forwardRef((props, ref) => {
    const { bg = 'light', size, styles = {}, error, helperText, trimWhitespace = false, onChange, onBlur, type, multiline, ...rest } = props;
    const { t } = useTranslation();
    const applyInputValue = (event, nextValue) => {
        if (nextValue === event.target.value) {
            onChange?.(event);
            return;
        }
        onChange?.({
            ...event,
            target: { ...event.target, value: nextValue },
            currentTarget: { ...event.currentTarget, value: nextValue }
        });
    };
    const handleChange = (event) => {
        if (shouldTrimFieldValue(type, trimWhitespace)) {
            applyInputValue(event, normalizeTrimmedValue(event.target.value, Boolean(multiline), 'change'));
            return;
        }
        onChange?.(event);
    };
    const handleBlur = (event) => {
        if (shouldTrimFieldValue(type, trimWhitespace)) {
            const normalized = normalizeTrimmedValue(event.target.value, Boolean(multiline), 'blur');
            if (normalized !== event.target.value) {
                applyInputValue(event, normalized);
            }
        }
        onBlur?.(event);
    };
    const blueStyles = {
        '& .MuiFilledInput-root': {
            color: 'white',
            ...filledInputAutofillStyles
        },
        '& .MuiInputLabel-root': {
            color: (theme) => theme.palette.grey[400],
            [`&.${inputLabelClasses.shrink}`]: {
                color: (theme) => theme.palette.grey[400],
                [`&.${inputLabelClasses.focused}`]: {
                    color: (theme) => theme.palette.grey[400]
                },
                [`&.${inputLabelClasses.error}`]: {
                    color: (theme) => theme.palette.error.main
                },
                [`&.${inputLabelClasses.disabled}`]: {
                    color: (theme) => theme.palette.text.disabled
                },
                [`&.${inputLabelClasses.filled}`]: {
                    transform: 'translate(12px, 6px) scale(0.75)'
                }
            }
        }
    };
    const lightStyles = {
        '& .MuiFilledInput-root': {
            border: 'none !important',
            transition: 'border 0.3s',
            ...filledInputAutofillStyles,
            '&:before, &:after': {
                display: 'none !important'
            },
            '&.Mui-focused': {
                border: 'none !important'
            }
        },
        '& .MuiInputLabel-root': {
            color: (theme) => theme.palette.grey[700],
            [`&.${inputLabelClasses.shrink}`]: {
                color: (theme) => theme.palette.grey[700],
                [`&.${inputLabelClasses.focused}`]: {
                    color: (theme) => theme.palette.grey[700]
                },
                [`&.${inputLabelClasses.error}`]: {
                    color: (theme) => theme.palette.error.main
                },
                [`&.${inputLabelClasses.disabled}`]: {
                    color: (theme) => theme.palette.text.disabled
                },
                [`&.${inputLabelClasses.filled}`]: {
                    transform: 'translate(12px, 6px) scale(0.75)'
                }
            }
        }
    };
    const theme = useTheme();
    const isLightColor = [
        '#00B6ED',
        '#FFDD00',
        '#76B82A' // GREEN
    ].includes(theme.palette.primary.main);
    const grey8Styles = {
        '& .MuiFilledInput-root': {
            borderRadius: '14px',
            boxShadow: 'none',
            color: (theme) => theme.palette.text.primary,
            backgroundColor: (theme) => isLightColor ? alpha('#9A9A98', 0.08) : theme.palette.common.grey8,
            border: (theme) => `1px solid ${isLightColor ? alpha('#9A9A98', 0.12) : alpha(theme.palette.grey[500], 0.12)}`,
            ...filledInputAutofillStyles,
            '&:hover': {
                backgroundColor: (theme) => isLightColor ? alpha('#9A9A98', 0.08) : theme.palette.common.grey8
            },
            '&.Mui-focused': {
                backgroundColor: (theme) => isLightColor ? alpha('#9A9A98', 0.08) : theme.palette.common.grey8,
                border: (theme) => `1px solid ${isLightColor ? alpha('#9A9A98', 0.12) : alpha(theme.palette.grey[500], 0.12)}`
            },
            '&:before, &:after': {
                display: 'none'
            }
        },
        '& .MuiInputLabel-root': {
            color: (theme) => (isLightColor ? theme.palette.common.grey.dark : theme.palette.grey[700])
        }
    };
    const grey8EventStyles = {
        '& .MuiFilledInput-root': {
            borderRadius: '12px',
            boxShadow: 'none',
            color: (theme) => theme.palette.grey[700],
            backgroundColor: (theme) => theme.palette.common.grey8,
            ...filledInputAutofillStyles,
            '&:hover': {
                backgroundColor: (theme) => theme.palette.common.grey8
            },
            '&.Mui-focused': {
                backgroundColor: (theme) => theme.palette.common.grey8
            },
            '&:before, &:after': {
                display: 'none'
            }
        },
        '& .MuiInputLabel-root': {
            color: (theme) => theme.palette.grey[500]
        }
    };
    const variantStyles = bg === 'blue'
        ? blueStyles
        : bg === 'grey8'
            ? grey8Styles
            : bg === 'grey8Event'
                ? grey8EventStyles
                : lightStyles;
    return (_jsxs(Box, { className: 'text-field-container', sx: { display: 'flex', flexDirection: 'column', width: '100%', gap: 0.5 }, children: [_jsx(TextField, { variant: 'filled', size: size, type: type, multiline: multiline, inputRef: ref, ...rest, onChange: handleChange, onBlur: handleBlur, sx: { ...variantStyles, ...styles } }), error && typeof helperText === 'string' && _jsx(FormValidationMessage, { error: helperText })] }));
});
HFilledTextField.displayName = 'HFilledTextField';
export default HFilledTextField;
